import { useEffect, useState } from "react";
import apiClient from "../api/apiClient";

/**
 * Whole Child Compass Attribute Interface
 * Represents individual compass attributes with scores
 */
interface CompassAttribute {
  name: string;
  confidence: "LOW" | "MEDIUM" | "HIGH";
  current_score: number;
}

/**
 * Whole Child Compass Score Data Interface
 * Represents the complete compass score response
 */
interface CompassScoreData {
  _id: string;
  userId: string;
  schoolHash: string;
  classId: string;
  attributes: CompassAttribute[];
  last_updated: string;
}

interface UseWholeChildCompassReturn {
  loading: boolean;
  error: string | null;
  data: CompassScoreData | null;
  refetch: () => void;
}

/**
 * Hook to fetch Whole Child Compass scores
 * Fetches compass attributes and current scores for a specific student
 * 
 * @param schoolHash - School identifier hash
 * @param grade - Grade/class identifier
 * @param userId - Student user ID
 * @returns Object with loading, error, data, and refetch function
 */
const useWholeChildCompass = (
  schoolHash: string,
  grade: string,
  userId: string
): UseWholeChildCompassReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<CompassScoreData | null>(null);

  const fetchCompassData = async () => {
    if (!schoolHash || !grade || !userId) return;

    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get(
        `/currentCompassScore/student/${schoolHash}/${grade}/${userId}`
      );
      setData(res.data.data || res.data);
    } catch (err: any) {
      console.error("Whole Child Compass API error:", err);
      setError(
        err?.response?.data?.message || "Failed to fetch Whole Child Compass scores"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompassData();
    // eslint-disable-next-line
  }, [schoolHash, grade, userId]);

  return {
    loading,
    error,
    data,
    refetch: fetchCompassData,
  };
};

export default useWholeChildCompass;
